import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import seedExtractionIcon from '/icons/seedextraction_icon.png';
import splotsKpiIcon from '/icons/splots_kpi.png';
import waveIcon from '/icons/wave_icon.png';
import { Box, Typography, Tooltip } from '@mui/material';
import { useGetSplotProcessingMetrics, useGetTrailProcessingMetrics } from '../../../api/useGetKpiProcessingMetrics';
import { useCommonStyles } from '../../../styles/GenericStyle';
import KpiDonutChart from './KpiDonutChart';
import KpiLoader from './KpiLoader';
import { useGlobalState } from '../../../contexts/GlobalState';
import {
    buildSplotStatusData,
    buildTrailStatusData,
    formatPercentage,
    formatValue,
    readMetricNumber,
} from './kpi.data';
import type { KPIProps } from './kpi.types';

const SPLOT_SUMMARY_FORMULAS: Record<string, string> = {
    'Avg days per SP Lot processing': 'Average number of days taken to complete SP Lots this year (from processing date to completion date, inclusive).',
    'Total Processing SP Lots': 'Total SP Lots this year that are currently in progress (excluding Created and Completed statuses).',
};

const TRAIL_SUMMARY_FORMULAS: Record<string, string> = {
    'Days to Completion': 'Average number of days taken per trial this year from first harvest to completion (inclusive).',
    '% Success by Materials': 'Percentage of materials completed out of total materials this year.',
    '% Success by Trials': 'Percentage of trials completed out of total trials this year.',
};

const TRAIL_STATUS_FORMULAS: Record<string, string> = {
    'Completed after 1st harvest': 'Percentage of materials completed in the first harvest cycle.',
    'Completed after 2nd harvest': 'Percentage of materials completed in the second harvest cycle.',
    'Completed after multiple harvest': 'Percentage of materials completed after more than two harvest cycles.',
    'Not Harvested': 'Percentage of materials that are still not harvested (Ready for Harvest or Unknown status).',
    'In Progress': 'Percentage of materials currently under processing or already harvested.',
};

const SPLOT_STATUS_FORMULA = 'Percentage share of SP Lots in each status out of total SP Lots.';

const KPI = ({ summaryCardsData, statusData, statusTitle, chartCenterLabel, statusColumns, kpiSection, hideButton }: KPIProps) => {
    const { state: { siteSelected } } = useGlobalState();
    const shouldFetchSplotMetrics = kpiSection === 'splot';
    const shouldFetchTrailMetrics = kpiSection === 'trails';

    const {
        data: metricsData = {},
        isLoading: isSplotMetricsLoading,
        isFetching: isSplotMetricsFetching,
    } = useGetSplotProcessingMetrics(shouldFetchSplotMetrics, siteSelected);

    const {
        data: trailMetricsData = {},
        isLoading: isTrailMetricsLoading,
        isFetching: isTrailMetricsFetching,
    } = useGetTrailProcessingMetrics(shouldFetchTrailMetrics, siteSelected);
    const avgDaysCompleted = readMetricNumber(metricsData?.Avg_days_completed ?? metricsData?.avg_days_completed);
    const totalProcessingSplots = readMetricNumber(metricsData?.total_processing_splots);
    const avgDaysToCompletion = readMetricNumber(trailMetricsData?.avg_days_to_completion);
    const materialSuccessPercentage = readMetricNumber(trailMetricsData?.material_success_percentage);
    const trialSuccessPercentage = readMetricNumber(trailMetricsData?.trial_success_percentage);
    const apiStatusData = shouldFetchSplotMetrics ? buildSplotStatusData(metricsData) : undefined;
    const trailApiStatusData = shouldFetchTrailMetrics ? buildTrailStatusData(trailMetricsData) : undefined;

    const apiSummaryCards = shouldFetchSplotMetrics && (avgDaysCompleted !== undefined || totalProcessingSplots !== undefined)
        ? [
            {
                labelLine1: 'Avg days per',
                labelLine2: 'SP Lot processing',
                value: formatValue(avgDaysCompleted),
            },
            {
                labelLine1: 'Total Processing',
                labelLine2: 'SP Lots',
                value: formatValue(totalProcessingSplots),
            },
        ]
        : undefined;

    const trailApiSummaryCards = shouldFetchTrailMetrics && (
        avgDaysToCompletion !== undefined || materialSuccessPercentage !== undefined || trialSuccessPercentage !== undefined
    )
        ? [
            {
                labelLine1: 'Days to Completion',
                labelLine2: '',
                value: formatValue(avgDaysToCompletion),
            },
            {
                labelLine1: '% Success',
                labelLine2: 'by Materials',
                value: formatValue(materialSuccessPercentage),
            },
            {
                labelLine1: '% Success',
                labelLine2: 'by Trials',
                value: formatValue(trialSuccessPercentage),
            },
        ]
        : undefined;

    const resolvedSummaryCards = apiSummaryCards && apiSummaryCards.length > 0
        ? apiSummaryCards
        : trailApiSummaryCards && trailApiSummaryCards.length > 0
            ? trailApiSummaryCards
            : (summaryCardsData && summaryCardsData.length > 0 ? summaryCardsData : []);

    const visibleSummaryCards = resolvedSummaryCards.slice(0, 3);
    const isThreeSummaryCards = visibleSummaryCards.length === 3;
    const sourceStatusData = statusData && statusData.length > 0
        ? statusData
        : apiStatusData && apiStatusData.length > 0
            ? apiStatusData
            : trailApiStatusData && trailApiStatusData.length > 0
                ? trailApiStatusData
                : [];
    const visibleStatusData = isThreeSummaryCards ? sourceStatusData.slice(0, 5) : sourceStatusData;
    const resolvedStatusTitle = statusTitle ?? (isThreeSummaryCards ? 'Materials Processing Status' : 'SP Lot Processing Status');
    const resolvedChartCenterLabel = chartCenterLabel ?? '100%';
    const firstSectionGap = '15px';
    const resolvedStatusColumns = statusColumns ?? (visibleStatusData.length <= 5 ? 1 : 2);
    const statusColumnSplitIndex = Math.ceil(visibleStatusData.length / resolvedStatusColumns);
    const statusItemsByColumn = resolvedStatusColumns === 1
        ? [visibleStatusData]
        : [
            visibleStatusData.slice(0, statusColumnSplitIndex),
            visibleStatusData.slice(statusColumnSplitIndex),
        ];
    const leftSectionDesktopWidth = isThreeSummaryCards ? '48.8%' : '24.4%';
    const rightSectionDesktopWidth = `calc(100% - ${leftSectionDesktopWidth} - 15px)`;
    const classes = useCommonStyles();
    const isMetricsLoading = (shouldFetchSplotMetrics && (isSplotMetricsLoading || isSplotMetricsFetching))
        || (shouldFetchTrailMetrics && (isTrailMetricsLoading || isTrailMetricsFetching));

    const getSummaryFormula = (labelLine1: string, labelLine2: string) => {
        const key = `${labelLine1} ${labelLine2}`.trim();
        if (kpiSection === 'splot') return SPLOT_SUMMARY_FORMULAS[key];
        if (kpiSection === 'trails') return TRAIL_SUMMARY_FORMULAS[key];
        return undefined;
    };

    const getStatusFormula = (statusLabel: string) => {
        if (kpiSection === 'splot') return SPLOT_STATUS_FORMULA;
        if (kpiSection === 'trails') return TRAIL_STATUS_FORMULAS[statusLabel];
        return undefined;
    };

    const getSummaryCardIcon = (cardIndex: number) => {
        const iconSx = { fontSize: 26, width: 26, height: 26, color: '#10B981' };

        if (!isThreeSummaryCards) {
            if (cardIndex === 0) {
                return <CalendarMonthOutlinedIcon sx={iconSx} />;
            }

            return <Box component="img" src={splotsKpiIcon} alt="SP Lots KPI" sx={{ width: 26, height: 26, objectFit: 'contain' }} />;
        }

        if (cardIndex === 0) {
            return <CalendarMonthOutlinedIcon sx={iconSx} />;
        }
        if (cardIndex === 1) {
            return (
                <Box
                    role="img"
                    aria-label="Seed Extraction"
                    sx={{
                        width: 26,
                        height: 26,
                        backgroundColor: '#10B981',
                        WebkitMask: `url(${seedExtractionIcon}) center / contain no-repeat`,
                        mask: `url(${seedExtractionIcon}) center / contain no-repeat`,
                    }}
                />
            );
        }
        if (cardIndex === 2) {
            return (
                <Box
                    role="img"
                    aria-label="Wave"
                    sx={{
                        width: 26,
                        height: 26,
                        backgroundColor: '#10B981',
                        WebkitMask: `url(${waveIcon}) center / contain no-repeat`,
                        mask: `url(${waveIcon}) center / contain no-repeat`,
                    }}
                />
            );
        }

        return <AccessTimeOutlinedIcon sx={iconSx} />;
    };

    if (isMetricsLoading) {
        return <KpiLoader isTrailLoader={kpiSection === 'trails'} />;
    }

    return (
        <Box
            sx={{
                width: '100%',
                mt: 2,
                mb: 2,
                boxSizing: 'border-box',
                borderRadius: '8px',
                opacity: 1,
                background: '#E9E9E9',
                boxShadow: '1px 1px 7.1px 0px #00000040',
            }}
        >
            {hideButton && (
                <Box sx={{ pt: { xs: '12px', sm: '14px', md: '16px' }, px: { xs: '12px', sm: '14px', md: '16px' }, pb: '8px' }}>
                    {hideButton}
                </Box>
            )}
            <Box
                sx={{
                    width: '100%',
                    minHeight: { lg: isThreeSummaryCards ? 200 : 190 },
                    boxSizing: 'border-box',
                    display: 'flex',
                    gap: { xs: '10px', sm: '12px', md: '15px' },
                    alignItems: 'stretch',
                    flexWrap: { xs: 'wrap', md: 'nowrap' },
                    pt: '4px',
                    px: { xs: '10px', sm: '14px', md: '16px' },
                    pb: { xs: '10px', sm: '14px', md: '16px' },
                }}
            >
                <Box
                    sx={{
                        width: { xs: '100%', md: leftSectionDesktopWidth },
                        maxWidth: { xs: '100%', md: leftSectionDesktopWidth },
                        flex: { xs: '1 1 100%', md: `0 0 ${leftSectionDesktopWidth}` },
                        display: isThreeSummaryCards ? 'grid' : 'flex',
                        flexDirection: 'column',
                        gridTemplateColumns: isThreeSummaryCards ? { xs: '1fr', sm: '1fr 1fr' } : 'none',
                        gridTemplateRows: isThreeSummaryCards ? 'repeat(2, minmax(0, 1fr))' : 'none',
                        columnGap: isThreeSummaryCards ? { xs: '12px', md: '15px' } : '0px',
                        rowGap: { xs: '12px', md: firstSectionGap },
                        gap: isThreeSummaryCards ? undefined : firstSectionGap,
                    }}
                >
                {visibleSummaryCards.map((card, index) => (
                    (() => {
                        const formula = getSummaryFormula(card.labelLine1, card.labelLine2);
                        const cardKey = `${card.labelLine1}-${card.labelLine2}-${index}`;
                        const summaryCardNode = (
                            <Box
                                key={cardKey}
                                sx={{
                                    flex: 1,
                                    gridColumn: isThreeSummaryCards && index === 2 ? '1 / 2' : 'auto',
                                    borderRadius: '8px',
                                    opacity: 1,
                                    background: '#FFFFFF',
                                    boxShadow: '1px 2px 4px 0px #00000040',
                                    py: { xs: '12px', sm: '14px', md: '17px' },
                                    px: { xs: '12px', sm: '14px', md: '18px' },
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: { xs: '8px', md: '10px' },
                                    minWidth: 0,
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '10px', md: '15px' }, minWidth: 0 }}>
                                    <Box
                                        sx={{
                                            width: { xs: 42, sm: 48, md: 54 },
                                            height: { xs: 42, sm: 48, md: 54 },
                                            borderRadius: '50%',
                                            background: '#D1FAE5',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        {getSummaryCardIcon(index)}
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography className={classes.labelNoto14Bold500}>{card.labelLine1}</Typography>
                                        <Typography className={classes.labelNoto14Bold500}>{card.labelLine2}</Typography>
                                    </Box>
                                </Box>
                                <Typography
                                    className={classes.labelNoto36Bold600}
                                    sx={{ lineHeight: 1, fontSize: { xs: '36px', sm: '40px', md: '44px', lg: '48px' }, whiteSpace: 'nowrap', flexShrink: 0 }}
                                >
                                    {card.value}
                                </Typography>
                            </Box>
                        );

                        return (
                            formula ? (
                                <Tooltip
                                    key={`${cardKey}-tooltip`}
                                    title={`${formula}`}
                                    arrow
                                    placement="top"
                                    slotProps={{ tooltip: { sx: { fontSize: '13px', lineHeight: 1.4, padding: '8px 12px', maxWidth: 360 } } }}
                                >
                                    {summaryCardNode}
                                </Tooltip>
                            ) : (
                                summaryCardNode
                            )
                        );
                    })()
                ))}
                </Box>

                <Box
                    sx={{
                        width: { xs: '100%', md: rightSectionDesktopWidth },
                        maxWidth: { xs: '100%', md: rightSectionDesktopWidth },
                        flex: { xs: '1 1 100%', md: `0 0 ${rightSectionDesktopWidth}` },
                        borderRadius: '8px',
                        opacity: 1,
                        background: '#FFFFFF',
                        boxShadow: '1px 2px 7.1px 0px #00000040',
                        pr: { xs: '12px', sm: '16px', md: '24px', lg: '38px' },
                        pl: { xs: '12px', sm: '16px', md: '24px', lg: '38px' },
                        py: { xs: 1.2, sm: 1.5 },
                        display: 'flex',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: { xs: '12px', sm: '16px', md: '20px', lg: '42px' },
                        flexDirection: { xs: 'column', sm: 'row' },
                    }}
                >
                    <KpiDonutChart statusItems={visibleStatusData} chartCenterLabel={resolvedChartCenterLabel} />

                    <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                        <Typography className={classes.labelNoto16Bold600} sx={{ mb: 1.2, fontSize: { xs: '14px', sm: '15px', md: '16px' } }}>
                            {resolvedStatusTitle}
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', lg: resolvedStatusColumns === 1 ? '1fr' : '1fr 1fr' },
                                columnGap: { xs: '12px', md: '18px', lg: resolvedStatusColumns === 1 ? '14px' : '56px' },
                                rowGap: { xs: '8px', lg: 0 },
                                alignItems: 'start',
                            }}
                        >
                            {statusItemsByColumn.map((columnItems, columnIndex) => (
                                <Box
                                    key={`status-column-${columnIndex}`}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr auto',
                                        columnGap: { xs: '8px', sm: '10px', md: '12px' },
                                        rowGap: '8px',
                                        alignItems: 'center',
                                        justifyContent: 'start',
                                    }}
                                >
                                    {columnItems.map((item) => (
                                        (() => {
                                            const formula = getStatusFormula(item.label);
                                            const valueNode = (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', flexShrink: 0, justifyContent: 'flex-end' }}>
                                                    <Typography className={classes.labelNoto14Bold700} sx={{ whiteSpace: 'nowrap', fontSize: { xs: '12px', sm: '13px', md: '14px' } }}>
                                                        {item.count}
                                                    </Typography>
                                                    <Typography className={classes.labelNoto14Bold400} sx={{ whiteSpace: 'nowrap', fontSize: { xs: '12px', sm: '13px', md: '14px' } }}>
                                                        {item.percentLabel ?? `(${formatPercentage(item.percent)}%)`}
                                                    </Typography>
                                                </Box>
                                            );

                                            return (
                                        <Box key={item.label} sx={{ display: 'contents' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: item.color, flexShrink: 0 }} />
                                                <Typography className={classes.labelNoto14Bold400} sx={{ lineHeight: '20px', fontSize: { xs: '12px', sm: '13px', md: '14px' }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {item.label}
                                                </Typography>
                                            </Box>
                                            {formula ? (
                                                <Tooltip
                                                    title={`${formula}`}
                                                    arrow
                                                    placement="top"
                                                    slotProps={{ tooltip: { sx: { fontSize: '13px', lineHeight: 1.4, padding: '8px 12px', maxWidth: 360 } } }}
                                                >
                                                    {valueNode}
                                                </Tooltip>
                                            ) : valueNode}
                                        </Box>
                                            );
                                        })()
                                    ))}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default KPI;
